import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PWAInstallPrompt from '../components/PWAInstallPrompt';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Video, Plus, ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';

const DigiMeet = () => {
  const navigate = useNavigate();
  const [roomId, setRoomId] = useState('');

  const handleCreateRoom = () => {
    const newRoomId = `DigiMeet-${Date.now()}`;
    navigate(`/meet/${newRoomId}`);
  };

  const handleJoinRoom = () => {
    if (roomId.trim()) {
      navigate(`/meet/${roomId}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Navigation />
      
      {/* PWA Install Prompt */}
      <PWAInstallPrompt
        appName="DigiMeet"
        appIcon="📹"
        appColor="#0f172a"
        storageKey="digimeet-install-prompt"
      />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-slate-900 rounded-2xl mb-6">
            <Video className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Digi Meet
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Professional video conferencing - Free, secure, and easy to use
          </p>
          <div className="mt-6 max-w-3xl mx-auto">
            <Card className="p-6 bg-blue-50 border-blue-200">
              <h3 className="font-semibold text-slate-900 mb-2">About Digi Meet</h3>
              <p className="text-sm text-slate-700 leading-relaxed">
                Digi Meet provides enterprise-grade video conferencing with crystal-clear HD video and audio. 
                Host unlimited meetings with no time restrictions, enjoy screen sharing, live chat, and recording capabilities. 
                Our platform ensures secure, encrypted connections for all your business communications, accessible from any device with just a click.
              </p>
            </Card>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Create New Meeting */}
          <Card className="p-8 bg-white hover:shadow-xl transition-shadow" data-testid="create-meeting-card">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="p-5 bg-gradient-to-br from-green-600 to-green-800 rounded-2xl">
                <Plus className="w-10 h-10 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  Start New Meeting
                </h3>
                <p className="text-slate-600 mb-6">
                  Create an instant video meeting room
                </p>
              </div>
              <Button
                onClick={handleCreateRoom}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6 text-lg rounded-full"
                data-testid="create-meeting-btn"
              >
                Create Meeting Room
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </Card>

          {/* Join Existing Meeting */}
          <Card className="p-8 bg-white hover:shadow-xl transition-shadow" data-testid="join-meeting-card">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="p-5 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl">
                <Video className="w-10 h-10 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                  Join Meeting
                </h3>
                <p className="text-slate-600 mb-6">
                  Enter a meeting room ID to join
                </p>
              </div>
              <div className="w-full space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="roomId">Meeting Room ID</Label>
                  <Input
                    id="roomId"
                    value={roomId}
                    onChange={(e) => setRoomId(e.target.value)}
                    placeholder="Enter room ID..."
                    className="text-center"
                    data-testid="room-id-input"
                  />
                </div>
                <Button
                  onClick={handleJoinRoom}
                  disabled={!roomId.trim()}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg rounded-full"
                  data-testid="join-meeting-btn"
                >
                  Join Meeting
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Features */}
        <Card className="p-8 bg-white">
          <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Digi Meet Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FeatureItem 
              title="HD Video & Audio"
              description="Crystal clear video and audio quality for professional meetings"
            />
            <FeatureItem 
              title="Screen Sharing"
              description="Share your screen, presentations, or applications"
            />
            <FeatureItem 
              title="No Downloads Required"
              description="Join directly from your browser, no installation needed"
            />
            <FeatureItem 
              title="Secure & Private"
              description="End-to-end encryption for all your conversations"
            />
            <FeatureItem 
              title="Unlimited Participants"
              description="Host meetings with unlimited number of participants"
            />
            <FeatureItem 
              title="Chat & Recording"
              description="Built-in chat and meeting recording capabilities"
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

const FeatureItem = ({ title, description }) => (
  <div className="flex gap-3">
    <div className="flex-shrink-0">
      <div className="w-2 h-2 bg-slate-900 rounded-full mt-2"></div>
    </div>
    <div>
      <h4 className="font-semibold text-slate-900 mb-1">{title}</h4>
      <p className="text-sm text-slate-600">{description}</p>
    </div>
  </div>
);

export default DigiMeet;
